import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useContext } from 'react';
import take from 'lodash/take';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';

import { Button } from 'formlabs-web-components/Link';
import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { Header } from 'formlabs-web-components/Type';
import form3PackageImg from '../../../../public/pkg-f3-basic_sq_1.png';

import ProductListItem from './ProductListItem';
import BundleProduct from './BundleProduct';
import ProductListPager from './ProductListPager';
import countryContext from '../../context/country';

import './ProductList.module.css';

const specialBundles = ['RS-F2-PKG-CR', 'PKG-F2-RWS']; // HACK These are bundle items, but should be displayed as normal product

const ProductList = ({
  baseUrl,
  disableDirectSale,
  category = '',
  limit,
  hideHeader,
  highlightBundles,
  products,
  wideItems,
  hasMore = false
}) => {
  const [t] = useTranslation();
  const [displayProducts, setProducts] = useState(
    products ? (limit > 0 ? take(products, limit) : products) : null
  );

  const isForm3Category = category.url === 'form-3';
  const { language, path } = useContext(countryContext);

  const overridePaths = [
    '/',
    '/uk/',
    '/eu/',
    '/it/',
    '/fr/',
    '/de/',
    '/ch/',
    '/es/'
  ];

  const defineDescription = language => {
    switch (language) {
      case 'en':
        return (
          '<p>Each Form 3 Low Force Stereolithography (LFS)™ 3D printer includes the essential tools required to start printing. </p>\r\n<p><strong>Includes:</strong></p>\r\n<ul>\r\n<li>1 Form 3 3D Printer</li>\r\n<li>1 Resin Tank</li>\r\n<li>1 Build Platform</li>\r\n<li>1 Finish Kit</li>\r\n<li>PreForm Software</li>\r\n</ul>\r\n<p>If you are shipping to the Asia-Pacific, Eastern Europe, Latin America, the Middle East, or Africa, please order via our <a href="' +
          path +
          'company/partners/">distribution partners</a>.</p>\r\n<p>Looking for biocompatible materials? Learn about our dental 3D printer, the <a href="https://dental.formlabs.com' +
          path +
          'form-3b/">Form 3B</a>.</p>'
        );
      case 'ch':
      case 'de':
        return (
          '<p><span id="docs-internal-guid-35e097d8-7fff-8baa-c3e0-b881efd7fa33">Jeder Form 3 LFS™ 3D-Drucker enthält das benötigte Zubehör damit Sie mit dem Drucken sofort beginnen können.</span></p>\r\n<p><span><span></span></span></p>\r\n<div><strong>Inhalt:</strong></div>\r\n<ul>\r\n<li>1 Form 3 3D Drucker</li>\r\n<li>1 Resin Tank</li>\r\n<li>1 Build Platform</li>\r\n<li>1 Finish Kit</li>\r\n<li>PreForm Software</li>\r\n</ul>\r\n<p>Falls Sie aus den folgenden Regionen bestellen möchten; Asien, Lateinamerika, Mittlerer Osten oder Afrika, wenden Sie sich bitte an einen unserer <a href="' +
          path +
          'company/partners/">Partner</a>.</p>\r\n<p>Sie suchen nach biokompatiblen Materialien? Lernen Sie unseren 3D-Drucker für die Zahnmedizin kennen, den <a href="https://dental.formlabs.com/de/form-3b/">Form 3B</a>.</p>'
        );
      case 'fr':
        return (
          '<p><span><span></span></span></p>\r\n<p dir="ltr"><span>L\'imprimante 3D LFS™ Form 3  est fournie avec les outils de base nécessaires pour commencer vos impressions.</span></p>\r\n<p><strong>Sont fournis :</strong></p>\r\n<ul>\r\n<li>1 imprimante 3D Form 3</li>\r\n<li>1 Bac à résine (Resin Tank)</li>\r\n<li>1 Plateau de construction (Build Platform)</li>\r\n<li>1 Kit de finition (Finish Kit)</li>\r\n<li>Logiciel PreForm</li>\r\n</ul>\r\n\r\n<p>Disponible sur commande en Asie-Pacifique, Amérique latine, Moyen-Orient et Afrique via nos <a href="' +
          path +
          'company/partners/">partenaires de distribution</a>.</p>\r\n<p>Vous êtes à la recherche de matériaux biocompatibles ? Découvrez notre imprimante 3D dentaire, <a href="https://dental.formlabs.com' +
          path +
          'form-3b/">la Form 3B</a>.</p>'
        );
      case 'it':
        return (
          '<p><span><span></span></span></p>\r\n<p dir="ltr"><span>Ciascuna stampante 3D Form 3 LFS™ include gli strumenti necessari per iniziare a stampare.</span></p>\r\n<p><strong>Include:</strong></p>\r\n<ul>\r\n<li>1 Stampante 3D Form 3</li>\r\n<li>1 Serbatoio resina</li>\r\n<li>1 Piattaforma di stampa</li>\r\n<li>1 Finish Kit</li>\r\n<li>Software PreForm</li>\r\n</ul>\r\n\r\n<p>Disponibile su ordinazione in Asia-Pacifico, America Latina, Medio Oriente e Africa tramite <a href="' +
          path +
          'company/partners/">partner di distribuzionen</a>.</p>\r\n<p>Cerchi materiali biocompatibili? Scopri di più sulla nostra stampante 3D per l&rsquo;odontoiatria, <a href="https://dental.formlabs.com' +
          path +
          'form-3b/">la Form 3B</a>.</p>'
        );
      case 'es':
        return (
          '<p><span><span></span></span></p>\n<p dir=“ltr"><span>Cada impresora 3D con Low Force Stereolithography (LFS)™ incluye las herramientas necesarias para empezar a imprimir.</span></p>\n<p dir="ltr"><span>El paquete completo incluye:</span></p>\n<p><span><span></span></span></p>\n<ul>\n<li dir="ltr">\n<p dir="ltr"><span>1 Impresora 3D Form 3</span></p>\n</li>\n<li dir="ltr">\n<p dir="ltr"><span>1 Tanque de resina</span></p>\n</li>\n<li dir="ltr">\n<p dir="ltr"><span>1 Plataforma de impresión</span></p>\n</li>\n<li dir="ltr">\n<p dir="ltr"><span>1 Kit de posacabado</span></p>\n</li>\n<li dir="ltr">\n<p dir="ltr"><span>Software PreForm</span></p>\n</li>\n</ul>\n<p>Se realizan envíos a Asia-Pacífico, Europa del Este, América Latina, Oriente Medio o África a través de nuestros <a href="' +
          path +
          'company/partners/">socios de distribución</a>.</p>\r\n<p>¿Buscas materiales biocompatibles? Infórmate sobre nuestra impresora 3D para odontología, <a href="https://dental.formlabs.com/eu/form-3b/">la Form 3B</a>.</p>'
        );
      default:
        return null;
    }
  };

  const definePrice = path => {
    switch (path) {
      case '/':
        return 3499;
      case '/eu/':
        return 3299;
      case '/uk/':
        return 2899;
      case '/ch/':
        return 4100;
      case '/de/':
        return 3299;
      case '/es/':
      case '/fr/':
      case '/it/':
        return 3299;
      default:
        return null;
    }
  };

  const fakeForm3 = {
    manualEntry: true,
    url: 'form-3/form-3-basic-package',
    sku: 'PKG-F3-BASIC',
    name: t('Form 3 Basic Package without Service'),
    price: definePrice(path),
    status: 1,
    attributes: {
      description: defineDescription(language),
      image: form3PackageImg,
      leadTime: '0'
    },
    media: [
      {
        id: 273,
        media_type: 'image',
        label: null,
        position: 1,
        disabled: false,
        types: ['image', 'small_image', 'thumbnail'],
        file: form3PackageImg
      }
    ]
  };

  if (!products) {
    return null;
  }

  const showMore =
    hasMore || (products && products.length > displayProducts.length);

  let header = null;
  if (!hideHeader && category) {
    header = (
      <div styleName={classnames('header')}>
        <div>
          <Header
            level={1}
            displayAs={3}
            weight="600"
            styles={{ marginBottom: 0 }}
          >
            {category.name}
          </Header>
          {category.attributes && category.attributes.description && (
            <div
              styleName="description"
              dangerouslySetInnerHTML={{
                __html: category.attributes.description
              }}
            />
          )}
        </div>
        {showMore ? (
          <ProductListPager
            styleName="pager"
            categoryId={category.id}
            resultCallback={result => {
              if (!isEqual(displayProducts, result.items)) {
                setProducts(result.items);
              }
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div styleName="container">
      {header}
      <ul styleName={classnames('products', { hideBorder: hideHeader })}>
        {displayProducts.map((product, index) => {
          const isBundle =
            product.type_id === 'bundle' &&
            specialBundles.indexOf(product.sku) === -1;

          return (
            <li
              styleName={classnames('product', {
                bundle: isBundle,
                wide: wideItems || product.displayWide,
                reccommend: isForm3Category
              })}
              key={product.sku}
            >
              {index === 1 && disableDirectSale && isForm3Category && (
                <p styleName="hat">{t('Recommended')}</p>
              )}
              {isBundle && highlightBundles ? (
                <BundleProduct
                  baseUrl={baseUrl}
                  product={product}
                  manualEntry={Boolean(product.manualEntry)}
                />
              ) : (
                <ProductListItem baseUrl={baseUrl} product={product} />
              )}
            </li>
          );
        })}
        {isForm3Category &&
          disableDirectSale &&
          overridePaths.indexOf(path) > -1 && (
            <li
              styleName={classnames('product', 'reccommend', 'bundle', {
                wide: wideItems || fakeForm3.displayWide
              })}
              key="fake"
            >
              <BundleProduct
                product={fakeForm3}
                baseUrl={baseUrl}
                manualEntry={Boolean(fakeForm3.manualEntry)}
                disableDirectSale={Boolean(disableDirectSale)}
                override={true}
              />
            </li>
          )}
      </ul>
      {showMore && category && category.url ? (
        <div styleName="more">
          <Button color="dark" to={`${baseUrl}${category.url}/`}>
            {category && category.name
              ? t('See all {{categoryName}}', { categoryName: category.name })
              : t('See all')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

ProductList.propTypes = {
  baseUrl: PropTypes.string.isRequired,
  limit: PropTypes.number
};

ProductList.defaultProps = {
  highlightBundles: true,
  limit: 0
};

export default DisplayComponent(ProductList);
